import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../lib/cart";
import { Trash, Plus, Minus, ShoppingCart, Storefront, Truck, ArrowRight } from "@phosphor-icons/react";
import { formatGBP } from "../lib/format";
import PostcodeFilter from "../components/PostcodeFilter";

export default function Cart() {
  const { items, groupedByVendor, updateQty, remove, subtotal, postcode } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="pt-24 pb-24 max-w-3xl mx-auto px-4 md:px-8 text-center" data-testid="empty-cart">
        <div className="w-20 h-20 rounded-full bg-[#F2EFE9] flex items-center justify-center mx-auto mb-5">
          <ShoppingCart size={34} weight="duotone" className="text-[#5C5C54]" />
        </div>
        <h1 className="font-heading text-3xl font-black tracking-tighter mb-3">Your basket is empty</h1>
        <p className="text-sm text-[#5C5C54] mb-6">Discover trusted UK vendors and start building your pantry.</p>
        <Link to="/" className="btn-primary">Explore marketplace</Link>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-24 max-w-7xl mx-auto px-4 md:px-8 lg:px-12" data-testid="cart-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-2">Your basket</h1>
        <p className="text-sm text-[#5C5C54] mb-4" data-testid="cart-vendor-count-text">
          You are ordering from <span className="font-bold text-[#1A1A18]">{groupedByVendor.length} vendor{groupedByVendor.length === 1 ? "" : "s"}</span>.
        </p>
        <div className="p-3 md:p-4 rounded-2xl bg-[#E4A951]/10 border border-[#E4A951]/40 flex items-start gap-3" data-testid="cart-separate-delivery-notice">
          <Truck size={18} weight="duotone" className="text-[#8A6A1F] mt-0.5 flex-shrink-0" />
          <p className="text-xs md:text-sm text-[#5C5C54]">
            <span className="font-bold text-[#1A1A18]">Items from different vendors may arrive separately</span> and may have different delivery times. Each vendor prepares and dispatches your order from their own kitchen.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-6 lg:gap-8">
        <div className="space-y-5">
          {groupedByVendor.map((group) => (
            <div key={group.vendor_id} className="card-surface overflow-hidden" data-testid="vendor-group">
              <div className="px-5 py-4 border-b border-[#E5E2D9] bg-[#F9F7F3]">
                <div className="flex items-center justify-between gap-3">
                  <Link to={`/vendors/${group.vendor_name.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center gap-2 hover:text-[#A94438] transition-colors min-w-0">
                    <Storefront size={18} weight="duotone" className="flex-shrink-0" />
                    <span className="font-heading font-bold text-sm md:text-base truncate">{group.vendor_name}</span>
                  </Link>
                  <span className="text-xs text-[#5C5C54] flex-shrink-0">Subtotal: <span className="font-bold text-[#1A1A18]">{formatGBP(group.subtotal)}</span></span>
                </div>
                <p className="text-[11px] text-[#5C5C54] mt-1.5 flex items-center gap-1">
                  <Truck size={12} weight="duotone" /> Delivery fee calculated at checkout · typically 2–3 days
                </p>
              </div>
              <div className="divide-y divide-[#E5E2D9]">
                {group.items.map((i) => (
                  <div key={i.product_id} className="p-4 md:p-5 flex items-center gap-4" data-testid="cart-line-item">
                    <Link to={`/products/${i.product_id}`} className="w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden bg-[#F2EFE9] flex-shrink-0">
                      <img src={i.image} alt={i.name} onError={(e) => { e.currentTarget.src = "https://images.pexels.com/photos/37099829/pexels-photo-37099829.jpeg"; }} className="w-full h-full object-cover" />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/products/${i.product_id}`} className="font-heading font-bold text-sm md:text-base leading-tight truncate hover:text-[#A94438]">{i.name}</Link>
                      <p className="text-xs text-[#5C5C54] mt-1">{formatGBP(i.price)} each</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          data-testid="cart-decrement"
                          onClick={() => updateQty(i.product_id, i.quantity - 1)}
                          className="w-8 h-8 rounded-full bg-[#F2EFE9] flex items-center justify-center hover:bg-[#E5E2D9]"
                        >
                          <Minus size={14} weight="bold" />
                        </button>
                        <span className="w-6 text-center text-sm font-bold">{i.quantity}</span>
                        <button
                          data-testid="cart-increment"
                          onClick={() => updateQty(i.product_id, Math.min(i.stock_quantity, i.quantity + 1))}
                          disabled={i.quantity >= i.stock_quantity}
                          className="w-8 h-8 rounded-full bg-[#1A1A18] text-white flex items-center justify-center hover:bg-[#A94438] disabled:opacity-40"
                        >
                          <Plus size={14} weight="bold" />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <p className="font-heading font-black text-base md:text-lg">{formatGBP(i.price * i.quantity)}</p>
                      <button
                        data-testid="cart-remove"
                        onClick={() => remove(i.product_id)}
                        className="p-2 rounded-full hover:bg-[#F2EFE9] text-[#A94438]"
                        aria-label="Remove"
                      >
                        <Trash size={16} weight="duotone" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* SUMMARY */}
        <aside className="lg:sticky lg:top-24 h-fit card-surface p-6" data-testid="cart-summary">
          <h2 className="font-heading text-xl font-bold tracking-tight mb-4">Order summary</h2>

          {!postcode && (
            <div className="mb-4 p-3 rounded-xl bg-[#E4A951]/15 border border-[#E4A951]/40 text-xs text-[#8A6A1F]">
              Set your postcode to see final delivery fees.
              <div className="mt-2"><PostcodeFilter compact /></div>
            </div>
          )}

          <div className="space-y-2 text-sm mb-4">
            <div className="flex justify-between text-[#5C5C54]">
              <span>Subtotal ({items.length} items)</span>
              <span className="font-bold text-[#1A1A18]">{formatGBP(subtotal)}</span>
            </div>
            <div className="flex justify-between text-[#5C5C54]">
              <span>Delivery ({groupedByVendor.length} vendor{groupedByVendor.length === 1 ? "" : "s"})</span>
              <span className="text-xs italic">calculated at checkout</span>
            </div>
            <div className="flex justify-between text-[#5C5C54]">
              <span>Service fee</span>
              <span className="font-bold text-[#1A1A18]">£1.50</span>
            </div>
          </div>

          <div className="border-t border-[#E5E2D9] pt-4 mb-5">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-xs font-bold tracking-wider uppercase text-[#5C5C54]">Estimated total</span>
              <span className="font-heading text-2xl font-black">{formatGBP(subtotal + 1.5)}+</span>
            </div>
            <p className="text-[11px] text-[#5C5C54]">Excludes per-vendor delivery fees, which are shown at checkout.</p>
          </div>

          <button
            data-testid="proceed-checkout-btn"
            onClick={() => navigate("/checkout")}
            className="btn-primary w-full gap-2"
          >
            Checkout <ArrowRight size={16} weight="bold" />
          </button>

          <p className="text-[10px] text-center text-[#5C5C54]/80 mt-4 flex items-center justify-center gap-1">
            <span>🔒</span> Secure Stripe checkout · Verified vendors only · GDPR-compliant
          </p>
        </aside>
      </div>
    </div>
  );
}
